<template>
    <div class="scans-table-wrapper">
        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
            <div class="spinner-large"></div>
            <p>Loading scans...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="scans.length === 0" class="empty-state">
            <font-awesome-icon icon="fa-solid fa-file-circle-plus" style="font-size: 80px; color: #94a3b8;" />
            <h3 class="empty-title">{{ emptyTitle }}</h3>
            <p class="empty-text">{{ emptyMessage }}</p>
            <button v-if="showEmptyAction" @click="$emit('empty-action')" class="btn-start-scanning">
                {{ emptyActionText }}
            </button>
        </div>

        <!-- Total Mismatch Warning -->
        <div v-if="!loading && scans.length > 0 && totalMismatchScans.length > 0" class="total-mismatch-warning-banner">
            <font-awesome-icon icon="fa-solid fa-exclamation-triangle" />
            <div class="warning-content">
                <strong>Total Mismatches:</strong>
                <span class="warning-summary">
          <span @click="showTotalMismatchDetails = !showTotalMismatchDetails" class="clickable-warning">
            {{ totalMismatchScans.length }} invoice{{ totalMismatchScans.length > 1 ? 's have' : ' has' }} total mismatches
          </span>
        </span>

                <!-- Total Mismatch Details -->
                <div v-if="showTotalMismatchDetails && totalMismatchDetails.length > 0" class="warning-details">
                    <div v-for="detail in totalMismatchDetails" :key="detail.id" class="detail-item">
                        <router-link :to="`/scans/${detail.id}`" class="invoice-link">
                            <strong>{{ detail.invoice }}:</strong>
                        </router-link>
                        <span class="missing-fields">
              Calculated: {{ detail.calculatedTotal }}, Grand Total: {{ detail.grandTotal }}, Difference: {{ detail.difference }}
            </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Subtotal Mismatch Warning -->
        <div v-if="!loading && scans.length > 0 && subtotalMismatchScans.length > 0" class="total-mismatch-warning-banner">
            <font-awesome-icon icon="fa-solid fa-exclamation-triangle" />
            <div class="warning-content">
                <strong>Subtotal Mismatches:</strong>
                <span class="warning-summary">
                    <span @click="showSubtotalMismatchDetails = !showSubtotalMismatchDetails" class="clickable-warning">
                        {{ subtotalMismatchScans.length }} invoice{{ subtotalMismatchScans.length > 1 ? 's have' : ' has' }} line items total ≠ subtotal
                    </span>
                </span>
                <div v-if="showSubtotalMismatchDetails && subtotalMismatchDetails.length > 0" class="warning-details">
                    <div v-for="detail in subtotalMismatchDetails" :key="detail.id" class="detail-item">
                        <router-link :to="`/scans/${detail.id}`" class="invoice-link">
                            <strong>{{ detail.invoice }}:</strong>
                        </router-link>
                        <span class="missing-fields">
                            Items Total: {{ detail.itemsTotal }}, Subtotal: {{ detail.subtotal }}, Difference: {{ detail.difference }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Zero-value Line Warning -->
        <div v-if="!loading && scans.length > 0 && zeroValueLineScans.length > 0" class="validation-warning-banner">
            <font-awesome-icon icon="fa-solid fa-exclamation-triangle" />
            <div class="warning-content">
                <strong>{{ $t('validation.zeroValueLinesTitle') }}</strong>
                <span class="warning-summary">
                    <span @click="showZeroValueLineDetails = !showZeroValueLineDetails" class="clickable-warning">
                        {{ $t('validation.zeroValueLinesSummary', zeroValueLineScans.length, { count: zeroValueLineScans.length }) }}
                    </span>
                </span>

                <!-- Zero-value Line Details -->
                <div v-if="showZeroValueLineDetails && zeroValueLineDetails.length > 0" class="warning-details">
                    <div v-for="detail in zeroValueLineDetails" :key="detail.id" class="detail-item">
                        <router-link :to="`/scans/${detail.id}`" class="invoice-link">
                            <strong>{{ detail.invoice }}:</strong>
                        </router-link>
                        <span class="missing-fields">{{ detail.names.join(', ') }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Company Validation Warning -->
        <div v-if="showCompanyValidationBanner" class="invalid-vat-warning-banner">
            <font-awesome-icon icon="fa-solid fa-exclamation-triangle" />
            <div class="warning-content">
                <strong>{{ $t('export.companyValidationTitle') }}</strong>
                <span class="warning-summary">
          <span @click="showCompanyValidationDetails = !showCompanyValidationDetails" class="clickable-warning">
            {{ validationWarnings.length === 1 ? $t('export.singleInvoiceNotContainingCompany', { companyName }) : $t('export.multipleInvoicesNotContainingCompany', { count: validationWarnings.length, companyName }) }}
          </span>
        </span>

                <!-- Company Validation Details -->
                <div v-if="showCompanyValidationDetails && validationWarnings.length > 0" class="warning-details">
                    <div v-for="warning in validationWarnings" :key="warning.id" class="detail-item">
                        <router-link :to="`/scans/${warning.id}`" class="invoice-link">
                            <strong>{{ warning.invoiceNumber }}:</strong>
                        </router-link>
                        <span class="missing-fields">{{ $t('export.companyNotFoundOnInvoice', { companyName }) }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Invalid VAT Warning -->
        <div v-if="!loading && scans.length > 0 && invalidVatScans.length > 0" class="invalid-vat-warning-banner">
            <font-awesome-icon icon="fa-solid fa-exclamation-triangle" />
            <div class="warning-content">
                <strong>{{ $t('validation.invalidVatTitle') }}</strong>
                <span class="warning-summary">
          <span @click="showInvalidVatDetails = !showInvalidVatDetails" class="clickable-warning">
            {{ $t('validation.invalidVatSummary', invalidVatScans.length, { count: invalidVatScans.length }) }}
          </span>
        </span>

                <!-- Invalid VAT Details -->
                <div v-if="showInvalidVatDetails && invalidVatDetails.length > 0" class="warning-details">
                    <div v-for="detail in invalidVatDetails" :key="detail.id" class="detail-item">
                        <router-link :to="`/scans/${detail.id}`" class="invoice-link">
                            <strong>{{ detail.invoice }}:</strong>
                        </router-link>
                        <span class="missing-fields">{{ detail.invalidFields.join(', ') }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Validation Warnings Summary -->
        <div v-if="!loading && scans.length > 0 && (missingInfoScans.length > 0 || vatConflicts.size > 0 || uniquePartnerNames.size > 0)" class="validation-warning-banner">
            <font-awesome-icon icon="fa-solid fa-exclamation-triangle" />
            <div class="warning-content">
                <strong>{{ $t('common.validationWarnings') }}</strong>
                <span class="warning-summary">
          <span v-if="missingInfoScans.length > 0" @click="showMissingInfoDetails = !showMissingInfoDetails" class="clickable-warning">
            {{ missingInfoScans.length }} {{ $t('common.missingInfo', missingInfoScans.length) }}
          </span><span v-if="missingInfoScans.length > 0 && (vatConflicts.size > 0 || uniquePartnerNames.size > 0)"> • </span><span v-if="vatConflicts.size > 0" @click="showVatConflictDetails = !showVatConflictDetails" class="clickable-warning">{{ vatConflicts.size }} {{ $t('common.vatConflict', vatConflicts.size) }}</span><span v-if="vatConflicts.size > 0 && uniquePartnerNames.size > 0"> • </span><span v-if="uniquePartnerNames.size > 0">{{ uniquePartnerNames.size }} {{ $t('common.uniqueName', uniquePartnerNames.size) }}</span>
        </span>

                <!-- Missing Info Details -->
                <div v-if="showMissingInfoDetails && missingInfoDetails.length > 0" class="warning-details">
                    <div v-for="detail in missingInfoDetails" :key="detail.invoice" class="detail-item">
                        <router-link :to="`/scans/${detail.id}`" class="invoice-link">
                            <strong>{{ detail.invoice }}:</strong>
                        </router-link>
                        <span class="missing-fields">{{ detail.missing.join(', ') }}</span>
                    </div>
                </div>

                <!-- VAT Conflict Details -->
                <div v-if="showVatConflictDetails && groupedVatConflicts.length > 0" class="warning-details">
                    <div v-for="group in groupedVatConflicts" :key="group.invoice" class="detail-item">
                        <router-link :to="`/scans/${group.id}`" class="invoice-link">
                            <strong>{{ group.invoice }}:</strong>
                        </router-link>
                        <span class="missing-fields">
              <div v-for="(conflict, idx) in group.conflicts" :key="idx">
                {{ conflict.role === 'buyer' ? 'Buyer' : 'Seller' }} VAT {{ conflict.vat }} has name "{{ conflict.currentName }}" but also appears as: {{ conflict.otherNames.join(', ') }}
              </div>
            </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Scans List -->
        <div v-if="!loading && (scans.length > 0 || queueItems.length > 0)" class="scans-list">
            <!-- Desktop Table Headers (hidden on mobile) -->
            <div class="table-headers">
                <div class="header-checkbox"></div>
                <div class="header-thumbnail"></div>
                <div class="header-info">
                    <span
                        class="sortable-header"
                        :class="{ 'sort-active': sortBy === 'invoice_number' }"
                        @click="handleSortClick('invoice_number')"
                    >
                        {{ $t('common.invoice') }}
                        <font-awesome-icon :icon="sortIconFor('invoice_number')" class="sort-icon" />
                    </span>
                    <span class="header-sep">·</span>
                    <span
                        class="sortable-header"
                        :class="{ 'sort-active': sortBy === 'invoice_date' }"
                        @click="handleSortClick('invoice_date')"
                    >
                        {{ $t('common.date', 'Дата') }}
                        <font-awesome-icon :icon="sortIconFor('invoice_date')" class="sort-icon" />
                    </span>
                </div>
                <div
                    class="header-amount sortable-header"
                    :class="{ 'sort-active': sortBy === 'grand_total' }"
                    @click="handleSortClick('grand_total')"
                >
                    {{ $t('common.amount') }}
                    <font-awesome-icon :icon="sortIconFor('grand_total')" class="sort-icon" />
                </div>
                <div
                    class="header-status sortable-header"
                    :class="{ 'sort-active': sortBy === 'status' }"
                    @click="handleSortClick('status')"
                >
                    {{ $t('common.status') }}
                    <font-awesome-icon :icon="sortIconFor('status')" class="sort-icon" />
                </div>
                <div class="header-actions">{{ $t('common.actions') }}</div>
            </div>
            <!-- Upload Queue Items -->
            <div
                v-for="item in queueItems"
                :key="`queue-${item.id}`"
                class="scan-row upload-row"
                :class="`status-${item.status}`"
            >
                <!-- Checkbox (disabled for uploading) -->
                <div class="scan-checkbox">
                    <div class="checkbox-placeholder"></div>
                </div>

                <!-- Status Badge -->
                <div class="scan-status" :class="`status-${item.status}`">
                    <span v-if="item.status === 'uploading'">Uploading</span>
                    <span v-else-if="item.status === 'processing'">Processing</span>
                    <span v-else-if="item.status === 'completed'">scan</span>
                    <span v-else-if="item.status === 'failed'">scan</span>
                    <span v-else>Pending</span>
                    <font-awesome-icon v-if="item.status === 'completed'" icon="fa-solid fa-check" />
                    <font-awesome-icon v-else icon="fa-solid fa-xmark" />
                </div>

                <!-- Progress indicator -->
                <div class="scan-time">
                    <div v-if="item.progress !== undefined" class="upload-progress">
                        {{ item.progress }}%
                    </div>
                    <div v-else class="spinner-tiny"></div>
                </div>

                <!-- Thumbnail -->
                <div class="scan-thumbnail">
                    <img v-if="item.preview" :src="item.preview" :alt="item.file?.name" class="thumbnail-img" />
                    <div v-else class="thumbnail-placeholder">
                        <font-awesome-icon icon="fa-solid fa-file" style="font-size: 24px;" />
                    </div>
                </div>

                <!-- File Info -->
                <div class="scan-info">
                    <div class="invoice-header">
                        <span class="invoice-type">{{ $t('common.uploading') }}</span>
                        <span class="invoice-number">{{ item.file?.name || 'File' }}</span>
                    </div>
                    <div class="invoice-details">
                        <span class="company-text">{{ formatFileSize(item.file?.size) }}</span>
                    </div>
                    <div v-if="item.error" class="upload-error">
                        {{ item.error }}
                    </div>
                </div>

                <!-- Actions -->
                <div class="scan-actions">
                    <button v-if="item.status !== 'completed'" @click.stop="$emit('cancel-upload', item.id)" class="btn-action btn-delete" title="Cancel">
                        <font-awesome-icon icon="fa-solid fa-xmark" />
                    </button>
                </div>
            </div>

            <!-- Regular Scan Items -->
            <template v-for="group in scansGroupedByDate" :key="group.date">
                <!-- Date Separator -->
                <div class="date-separator">
                    <div class="date-header">
                        <input
                            type="checkbox"
                            :checked="isDateGroupSelected(group)"
                            @change="toggleDateGroupSelection(group)"
                            class="date-checkbox"
                        />
                        <span class="date-label">{{ group.label }}</span>
                    </div>
                </div>

                <div
                    v-for="scan in group.scans"
                    :key="scan.id"
                    :class="['scan-row', { 'selected': isSelected(scan.id), 'is-checked': scan.is_checked, 'has-warning': hasMissingInfo(scan.id) || hasVatConflictForScan(scan.id) || hasUniqueName(scan.id), 'has-invalid-vat': hasInvalidVat(scan.id), 'has-total-mismatch': hasTotalMismatch(scan.id) || hasSubtotalMismatch(scan.id), 'has-company-warning': hasCompanyValidationWarning(scan.id), 'has-zero-value-line': hasZeroValueLine(scan.id) }]"
                >
                    <!-- Checkbox -->
                    <div class="scan-checkbox" @click.stop>
                        <input
                            type="checkbox"
                            :checked="isSelected(scan.id)"
                            @change="toggleSelection(scan.id)"
                            class="checkbox-input"
                        />
                    </div>

                    <!-- Thumbnail -->
                    <div class="scan-thumbnail" @click="viewScan(scan.id)">
                        <img
                            v-if="pdfThumbnails[scan.id]"
                            :src="pdfThumbnails[scan.id]"
                            :alt="scan.invoice_number || 'Invoice'"
                            class="thumbnail-img"
                        />
                        <img
                            v-else-if="scan.id"
                            :src="`/api/invoices/${scan.id}/original?thumbnail=true`"
                            :alt="scan.invoice_number || 'Invoice'"
                            class="thumbnail-img"
                            @error="handleThumbnailError($event, scan.id)"
                        />
                        <div v-else class="thumbnail-placeholder">
                            <font-awesome-icon icon="fa-solid fa-file" style="font-size: 24px;" />
                        </div>
                    </div>

                    <!-- Status Badge -->
                    <div class="scan-status" :class="`status-${scan.status}`">
                        <span v-if="scan.status === 'completed'">✓ Completed</span>
                        <span v-else-if="scan.status === 'processing'">Processing</span>
                        <span v-else-if="scan.status === 'archived'">{{ $t('common.archived') }}</span>
                        <span v-else-if="scan.status === 'failed'">Failed</span>
                        <span v-else>Pending</span>
                    </div>

                    <!-- Invoice Info -->
                    <div class="scan-info" @click="viewScan(scan.id)">
                        <div class="invoice-header">
                            <span class="invoice-number">{{ scan.invoice_number || '—' }}</span>
                            <span class="invoice-date">{{ getInvoiceDate(scan) }}</span>
                            <span class="invoice-type">{{ getDocumentType(scan.invoice_type) }}</span>

                            <span v-if="hasMissingInfo(scan.id) || hasVatConflictForScan(scan.id) || hasUniqueName(scan.id)" class="warning-icon" :title="getWarningDescription(scan.id)">
              <font-awesome-icon icon="fa-solid fa-exclamation-triangle" />
            </span>
                        </div>
                        <div class="invoice-details">
                            <span class="company-text" :class="{ 'unique-name': hasUniqueName(scan.id) }">{{ getCounterpartyName(scan) }}</span>
                            <span v-if="getCounterpartyVatId(scan)" class="invoice-tax-id-inline" :class="{ 'vat-invalid-text': getCounterpartyVatValidation(scan) === false }">
              <font-awesome-icon icon="fa-solid fa-hashtag" style="width: 12px; height: 12px; font-style: normal; color: #0ea5e9;" />
              <span>{{ getCounterpartyVatId(scan) }}</span>
            </span>
                        </div>
                        <div class="invoice-type-selector" @click.stop>
                            <select
                                :value="scan.type || 'expense'"
                                @change="handleTypeChange(scan.id, $event.target.value)"
                                class="type-select"
                            >
                                <option value="expense">{{ $t('invoices.expense') }}</option>
                                <option value="income">{{ $t('invoices.income') }}</option>
                            </select>
                        </div>
                    </div>

                    <!-- Amount Column -->
                    <div class="invoice-amount-wrapper">
                        <div class="invoice-amount">
                            <span class="amount-value">{{ formatAmount(scan.grand_total) }}</span>
                            <span class="amount-currency">{{ scan.currency || 'BGN' }}</span>
                        </div>
                        <div class="invoice-vat">
                            <span class="vat-label">VAT:</span>
                            <span class="vat-amount">{{ formatAmount(scan.tax_amount) }} {{ scan.currency || 'BGN' }}</span>
                        </div>
                        <div class="invoice-payment" v-if="scan.payment_method">
                            <font-awesome-icon icon="fa-solid fa-credit-card" style="width: 14px; height: 14px;" />
                            <span>{{ scan.payment_method }}</span>
                        </div>
                    </div>

                    <!-- Actions -->
                    <div class="scan-actions">
                        <button @click.stop="viewScan(scan.id)" class="btn-action btn-view" title="Edit">
                            <font-awesome-icon icon="fa-solid fa-pen" />
                        </button>
                        <button
                            @click.stop="handleSingleExport(scan.id)"
                            class="btn-action btn-export"
                            title="Export Invoice"
                        >
                            <font-awesome-icon icon="fa-solid fa-download" />
                        </button>
                        <button
                            @click.stop="handleDelete(scan.id)"
                            class="btn-action btn-delete"
                            title="Delete"
                        >
                            <font-awesome-icon icon="fa-solid fa-trash" />
                        </button>
                    </div>
                </div>
            </template>
        </div>

        <!-- Totals Summary -->
        <div v-if="!loading && scans.length > 0" class="totals-summary">
            <div class="totals-label">{{ $t('invoices.summary', 'Summary') }}:</div>
            <div class="totals-container">
                <div v-for="(totals, currency) in totalsByCurrency" :key="currency" class="currency-group">
                    <div class="currency-header">{{ currency }}</div>
                    <div class="totals-items">
                        <div class="total-item">
                            <span class="total-label">{{ $t('invoices.subtotalWithoutVat', 'Subtotal (without VAT)') }}</span>
                            <span class="total-value">{{ formatAmount(totals.subtotal) }} {{ currency }}</span>
                        </div>
                        <div class="total-item">
                            <span class="total-label">{{ $t('invoices.vatAmount', 'VAT') }}</span>
                            <span class="total-value">{{ formatAmount(totals.vat) }} {{ currency }}</span>
                        </div>
                        <div class="total-item total-grand">
                            <span class="total-label">{{ $t('invoices.totalWithVat', 'Total (with VAT)') }}</span>
                            <span class="total-value">{{ formatAmount(totals.grand) }} {{ currency }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Pagination -->
        <div v-if="!loading && totalPages > 1" class="pagination">
            <button
                @click="changePage(currentPage - 1)"
                :disabled="currentPage === 1"
                class="pagination-btn"
            >
                <font-awesome-icon icon="fa-solid fa-chevron-left" />
                {{ $t('common.previous') }}
            </button>

            <div class="pagination-pages">
                <button
                    v-for="page in visiblePages"
                    :key="page"
                    @click="changePage(page)"
                    :class="['pagination-page', { active: page === currentPage }]"
                >
                    {{ page }}
                </button>
            </div>

            <button
                @click="changePage(currentPage + 1)"
                :disabled="currentPage === totalPages"
                class="pagination-btn"
            >
                {{ $t('common.next') }}
                <font-awesome-icon icon="fa-solid fa-chevron-right" />
            </button>
        </div>

        <!-- Delete Confirmation Modal -->
        <div v-if="showDeleteModal" class="modal-overlay" @click="closeDeleteModal">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h3>{{ $t('invoices.deleteConfirm', 'Are you sure you want to delete this scan?') }}</h3>
                    <button @click="closeDeleteModal" class="btn btn-icon">
                        <font-awesome-icon icon="fa-solid fa-xmark" />
                    </button>
                </div>
                <div class="modal-actions">
                    <button @click="confirmDelete" class="btn-confirm">{{ $t('common.yes', 'Yes') }}</button>
                    <button @click="closeDeleteModal" class="btn-cancel">{{ $t('common.no', 'No') }}</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, reactive, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useFormatters } from '@/composables/useFormatters';
import * as pdfjsLib from 'pdfjs-dist';

// Reuse the same locally-bundled worker as the upload preprocessor.
pdfjsLib.GlobalWorkerOptions.workerSrc = '/build/assets/pdf.worker.min.mjs';
import {
    hasMissingInfo as validateMissingInfo,
    getMissingInfoInvoices,
    detectVatConflicts,
    getVatConflictDetails,
    hasVatConflict,
    detectUniquePartnerNames
} from '@/services/exportValidationService';

const props = defineProps({
    scans: {
        type: Array,
        required: true
    },
    queueItems: {
        type: Array,
        default: () => []
    },
    loading: {
        type: Boolean,
        default: false
    },
    currentPage: {
        type: Number,
        default: 1
    },
    totalPages: {
        type: Number,
        default: 1
    },
    selectedScans: {
        type: Array,
        default: () => []
    },
    showCheckboxes: {
        type: Boolean,
        default: false
    },
    showDeleteAction: {
        type: Boolean,
        default: true
    },
    showEmptyAction: {
        type: Boolean,
        default: true
    },
    emptyTitle: {
        type: String,
        default: 'No scans found'
    },
    emptyMessage: {
        type: String,
        default: 'Upload your first invoice to get started'
    },
    emptyActionText: {
        type: String,
        default: 'Start Scanning'
    },
    validationWarnings: {
        type: Array,
        default: () => []
    },
    companyName: {
        type: String,
        default: ''
    },
    enforceSubtotalValidation: {
        type: Boolean,
        default: false
    },
    validateVatConflicts: {
        type: Boolean,
        default: true
    },
    sortBy: {
        type: String,
        default: ''
    },
    sortOrder: {
        type: String,
        default: 'desc'
    }
});

const emit = defineEmits(['page-change', 'toggle-selection', 'view', 'delete', 'empty-action', 'export', 'cancel-upload', 'type-change', 'sort-change']);

const handleSortClick = (column) => {
    let nextOrder;
    if (props.sortBy !== column) {
        nextOrder = 'asc';
    } else if (props.sortOrder === 'asc') {
        nextOrder = 'desc';
    } else {
        nextOrder = 'asc';
    }
    emit('sort-change', { sortBy: column, sortOrder: nextOrder });
};

const sortIconFor = (column) => {
    if (props.sortBy !== column) return 'fa-solid fa-sort';
    return props.sortOrder === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down';
};

const router = useRouter();
const { t } = useI18n();
const { formatAmount, formatDate, formatFileSize } = useFormatters();

// Delete confirmation modal state
const showDeleteModal = ref(false);
const scanToDelete = ref(null);

// Validation details state
const showMissingInfoDetails = ref(false);
const showInvalidVatDetails = ref(false);
const showVatConflictDetails = ref(false);
const showTotalMismatchDetails = ref(false);
const showSubtotalMismatchDetails = ref(false);
const showCompanyValidationDetails = ref(false);
const showZeroValueLineDetails = ref(false);

// Debug logging for validation warnings
watch(() => props.validationWarnings, (newWarnings) => {
    console.log('🎯 ScansTable received validationWarnings:', newWarnings);
    console.log('🎯 Company name prop:', props.companyName);
    console.log('🎯 Should show banner:', !props.loading && props.scans.length > 0 && newWarnings.length > 0 && props.companyName);
}, { immediate: true, deep: true });

// Computed property for stable company validation banner visibility (fixes Edge browser flicker)
const showCompanyValidationBanner = computed(() => {
    return !props.loading && 
           props.scans.length > 0 && 
           props.validationWarnings.length > 0 && 
           Boolean(props.companyName);
});

const visiblePages = computed(() => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, props.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(props.totalPages, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
        pages.push(i);
    }

    return pages;
});

// Transform scans into validation format
const validationScans = computed(() => {
    return props.scans.map(scan => ({
        id: scan.id,
        invoice_number: scan.invoice_number || '',
        document_number: scan.document_number || '',
        invoice_date: scan.invoice_date || '',
        company_vat: scan.buyer_vat_number || scan.company_vat || scan.buyer_tax_id || '',
        vendor_vat: scan.seller_vat_number || scan.seller_tax_id || '',
        vendor_name: scan.seller_name || '',
        // Also include buyer info for checking conflicts on both sides
        buyer_vat: scan.buyer_vat_number || scan.buyer_tax_id || '',
        buyer_name: scan.buyer_name || '',
        seller_vat: scan.seller_vat_number || scan.seller_tax_id || '',
        seller_name: scan.seller_name || '',
        currency: scan.currency || '',
        vat_amount: scan.tax_amount || '',
        total_amount: scan.grand_total || ''
    }));
});

// Missing info per scan
const missingInfoScans = computed(() => {
    return validationScans.value.filter(scan => validateMissingInfo(scan));
});

// Detailed missing info
const missingInfoDetails = computed(() => {
    return getMissingInfoInvoices(validationScans.value, t);
});

// VAT conflicts across scans (same VAT №, different name). Optional: hidden when
// the user turns off "VAT conflict validation".
const vatConflicts = computed(() => {
    if (!props.validateVatConflicts) return new Set();
    return detectVatConflicts(validationScans.value);
});

// Detailed VAT conflict info
const vatConflictDetails = computed(() => {
    return getVatConflictDetails(validationScans.value);
});

// Group VAT conflicts by invoice
const groupedVatConflicts = computed(() => {
    const groups = new Map();
    vatConflictDetails.value.forEach(detail => {
        if (!groups.has(detail.invoice)) {
            groups.set(detail.invoice, {
                id: detail.id,
                invoice: detail.invoice,
                conflicts: []
            });
        }
        groups.get(detail.invoice).conflicts.push({
            role: detail.role,
            vat: detail.vat,
            currentName: detail.currentName,
            otherNames: detail.otherNames
        });
    });
    return Array.from(groups.values());
});

// Unique partner names across scans
const uniquePartnerNames = computed(() => {
    return detectUniquePartnerNames(validationScans.value);
});

// Invalid VAT scans
const invalidVatScans = computed(() => {
    return props.scans.filter(scan =>
        getSellerVatValidation(scan) === false || getBuyerVatValidation(scan) === false
    );
});

// Invalid VAT details with invoice labels
const invalidVatDetails = computed(() => {
    return invalidVatScans.value.map(scan => {
        const invalidFields = [];

        if (getSellerVatValidation(scan) === false) {
            invalidFields.push(t('validation.sellerVatInvalid'));
        }
        if (getBuyerVatValidation(scan) === false) {
            invalidFields.push(t('validation.buyerVatInvalid'));
        }

        // Generate invoice label with priority: invoice_number > document_number > date > short ID
        let invoice;
        if (scan.invoice_number) {
            invoice = scan.invoice_number;
        } else if (scan.document_number) {
            invoice = scan.document_number;
        } else if (scan.invoice_date) {
            invoice = `Invoice from ${scan.invoice_date}`;
        } else {
            invoice = `Invoice ${scan.id.substring(0, 8)}...`;
        }

        return {
            id: scan.id,
            invoice,
            invalidFields
        };
    });
});

// Total mismatch scans - replicating the validation logic from ScanDetails.vue
const totalMismatchScans = computed(() => {
    return props.scans.filter(scan => {
        // Rows with NO line items (register invoices): a "total mismatch" only makes
        // sense as net + VAT ≠ grand total. Compute that real difference instead of
        // trusting the server flag blindly — an old flag set from the fiscal-receipt
        // cross-check (amounts_verified) marked rows whose amounts are actually
        // consistent (difference 0.00), producing fake mismatches. Only flag when the
        // stored amounts genuinely don't add up.
        if (!scan.line_items || !Array.isArray(scan.line_items) || scan.line_items.length === 0) {
            const sub = parseFloat(scan.subtotal) || 0;
            const vat = parseFloat(scan.tax_amount) || 0;
            const grand = parseFloat(scan.grand_total) || 0;
            // No amounts at all to judge → fall back to the server flag.
            if (sub === 0 && vat === 0 && grand === 0) {
                return !!scan.has_total_mismatch;
            }
            const diff = Math.round(Math.abs((sub + vat) - grand) * 100) / 100;
            return diff > 0.01;
        }

        const subtotal = parseFloat(scan.subtotal) || 0;
        const grandTotal = parseFloat(scan.grand_total) || 0;
        let adjustedGrandTotal = grandTotal;

        const itemsTotal = getRawItemsTotal(scan.line_items);
        const subtotalRounded = Math.round(subtotal * 100) / 100;
        const itemsTotalRounded = Math.round(itemsTotal * 100) / 100;

        // Smart VAT detection: check if subtotal matches line items
        let calculatedTotal;
        if (subtotal > 0 && Math.abs(subtotalRounded - itemsTotalRounded) < 0.02) {
            // Subtotal matches line items, so add VAT
            const vatAmount = parseFloat(scan.tax_amount) || 0;
            calculatedTotal = Math.round((itemsTotal + vatAmount) * 100) / 100;
        } else {
            // A line printed at its pre-discount amount with the deduction shown
            // separately overshoots by the discount. Subtract it only when that
            // lands exactly on the stated total, so a discount already folded into
            // the line total is never subtracted twice. Mirrors ScanDetails.vue.
            const netOfDiscounts = Math.round(getGrossItemsTotal(scan.line_items) * 100) / 100;

            calculatedTotal = (adjustedGrandTotal > 0 && Math.abs(netOfDiscounts - adjustedGrandTotal) <= 0.01)
                ? netOfDiscounts
                : itemsTotalRounded;
        }

        // Check if there's a mismatch (allow 1 cent rounding tolerance)
        const difference = Math.round(Math.abs(calculatedTotal - adjustedGrandTotal) * 100) / 100;
        return difference > 0.01;
    });
});

// Total mismatch details with invoice labels and differences
const totalMismatchDetails = computed(() => {
    return totalMismatchScans.value.map(scan => {
        const subtotal = parseFloat(scan.subtotal) || 0;
        const grandTotal = parseFloat(scan.grand_total) || 0;
        let adjustedGrandTotal = grandTotal;
        if (grandTotal > 1000 && !scan.grand_total.toString().includes('.')) {
            adjustedGrandTotal = grandTotal / 100;
        }

        const hasItems = Array.isArray(scan.line_items) && scan.line_items.length > 0;
        const itemsTotal = getRawItemsTotal(scan.line_items || []);
        const subtotalRounded = Math.round(subtotal * 100) / 100;
        const itemsTotalRounded = Math.round(itemsTotal * 100) / 100;

        let calculatedTotal;
        if (!hasItems) {
            // Flagged by the server (register row: amounts didn't match the receipt).
            // There are no line items to compute a numeric difference from.
            calculatedTotal = adjustedGrandTotal;
        } else if (subtotal > 0 && Math.abs(subtotalRounded - itemsTotalRounded) < 0.02) {
            const vatAmount = parseFloat(scan.tax_amount) || 0;
            calculatedTotal = Math.round((itemsTotal + vatAmount) * 100) / 100;
        } else {
            calculatedTotal = itemsTotalRounded;
        }

        const difference = Math.round((calculatedTotal - adjustedGrandTotal) * 100) / 100;

        // Generate invoice label
        let invoice;
        if (scan.invoice_number) {
            invoice = scan.invoice_number;
        } else if (scan.document_number) {
            invoice = scan.document_number;
        } else if (scan.invoice_date) {
            invoice = `Invoice from ${scan.invoice_date}`;
        } else {
            invoice = `Invoice ${scan.id.substring(0, 8)}...`;
        }

        return {
            id: scan.id,
            invoice,
            difference: difference.toFixed(2),
            calculatedTotal: calculatedTotal.toFixed(2),
            grandTotal: adjustedGrandTotal.toFixed(2)
        };
    });
});

// Check if a specific scan has missing info
const hasMissingInfo = (scanId) => {
    const scan = validationScans.value.find(s => s.id === scanId);
    return scan ? validateMissingInfo(scan) : false;
};

// Check if a specific scan has VAT conflict
const hasVatConflictForScan = (scanId) => {
    return vatConflicts.value.has(scanId);
};

// Check if a specific scan has unique partner name
const hasUniqueName = (scanId) => {
    return uniquePartnerNames.value.has(scanId);
};

// Check if a specific scan has invalid VAT
const hasInvalidVat = (scanId) => {
    return invalidVatScans.value.some(scan => scan.id === scanId);
};

// Check if a specific scan has company validation warning
const hasCompanyValidationWarning = (scanId) => {
    return props.validationWarnings.some(warning => warning.id === scanId);
};

// Check if a specific scan has total mismatch
const hasTotalMismatch = (scanId) => {
    return totalMismatchScans.value.some(scan => scan.id === scanId);
};

// Subtotal mismatch scans - line items total != subtotal
// Sum line item totals WITHOUT applying discounts (totals may already include discounts)
const getRawItemsTotal = (lineItems) => {
    if (!Array.isArray(lineItems)) return 0;
    return lineItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
};

// Sum line item totals (discounts already factored into totals by extraction)
const getEffectiveItemsTotal = (lineItems, subtotal) => {
    if (!Array.isArray(lineItems)) return 0;
    return lineItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
};

// Line item totals with their separately-extracted discounts applied — what the
// grand total is built from on gross (VAT-inclusive) invoices. Percentage
// discounts ("5%") carry no amount, so they're skipped.
const getGrossItemsTotal = (lineItems) => {
    if (!Array.isArray(lineItems)) return 0;
    return lineItems.reduce((sum, item) => {
        const total = parseFloat(item.total) || 0;
        const rawDiscount = item.discount;
        const isPercentage = typeof rawDiscount === 'string' && rawDiscount.includes('%');
        const discount = isPercentage ? 0 : Math.abs(parseFloat(rawDiscount) || 0);
        return sum + total - discount;
    }, 0);
};

const subtotalMismatchScans = computed(() => {
    if (!props.enforceSubtotalValidation) return [];
    return props.scans.filter(scan => {
        if (!scan.line_items || !Array.isArray(scan.line_items) || scan.line_items.length === 0) {
            return false;
        }
        const subtotal = parseFloat(scan.subtotal) || 0;
        if (subtotal === 0) return false;

        // Gross line items that already add up to the grand total are a valid
        // representation, not a mismatch — see the same guard in ScanDetails.vue.
        // Both discount conventions count as consistent: totals printed before
        // the discount, and totals already net of it.
        const grandTotal = parseFloat(scan.grand_total) || 0;
        if (grandTotal > 0) {
            const grossTotal = Math.round(getRawItemsTotal(scan.line_items) * 100) / 100;
            const grossLessDiscounts = Math.round(getGrossItemsTotal(scan.line_items) * 100) / 100;
            if (Math.abs(grossTotal - grandTotal) <= 0.01 || Math.abs(grossLessDiscounts - grandTotal) <= 0.01) {
                return false;
            }
        }

        const itemsTotal = Math.round(getEffectiveItemsTotal(scan.line_items, subtotal) * 100) / 100;
        const subtotalRounded = Math.round(subtotal * 100) / 100;

        return Math.abs(itemsTotal - subtotalRounded) > 0.01;
    });
});

const subtotalMismatchDetails = computed(() => {
    return subtotalMismatchScans.value.map(scan => {
        const subtotal = parseFloat(scan.subtotal) || 0;
        const itemsTotal = getEffectiveItemsTotal(scan.line_items, subtotal);
        const itemsTotalRounded = Math.round(itemsTotal * 100) / 100;
        const subtotalRounded = Math.round(subtotal * 100) / 100;
        const difference = Math.round((itemsTotalRounded - subtotalRounded) * 100) / 100;

        let invoice;
        if (scan.invoice_number) {
            invoice = scan.invoice_number;
        } else if (scan.document_number) {
            invoice = scan.document_number;
        } else if (scan.invoice_date) {
            invoice = `Invoice from ${scan.invoice_date}`;
        } else {
            invoice = `Invoice ${scan.id.substring(0, 8)}...`;
        }

        return {
            id: scan.id,
            invoice,
            itemsTotal: itemsTotalRounded.toFixed(2),
            subtotal: subtotalRounded.toFixed(2),
            difference: difference.toFixed(2)
        };
    });
});

const hasSubtotalMismatch = (scanId) => {
    return subtotalMismatchScans.value.some(scan => scan.id === scanId);
};

/**
 * A line that carries no value at all — no quantity, no price, no amount.
 *
 * Fuel receipts list the pump's full product range, so an item the customer
 * did not buy still lands on the invoice as a row of zeros. It is harmless to
 * the totals but goes on to the export and into stock as a phantom article, so
 * it is worth pointing out and letting the accountant remove it.
 *
 * A genuinely free line still prices its goods, so a zero amount alone is not
 * enough; every one of the three has to be empty.
 */
const isZeroValueLine = (item) => {
    if (!item) return false;
    const zero = (value) => {
        if (value === null || value === undefined || value === '') return true;
        const number = parseFloat(String(value).replace(',', '.'));
        return !isNaN(number) && number === 0;
    };
    return zero(item.quantity) && zero(item.unit_price) && zero(item.total);
};

const zeroValueLineScans = computed(() => {
    return props.scans.filter(scan =>
        Array.isArray(scan.line_items) && scan.line_items.some(isZeroValueLine)
    );
});

const zeroValueLineDetails = computed(() => {
    return zeroValueLineScans.value.map(scan => {
        let invoice;
        if (scan.invoice_number) {
            invoice = scan.invoice_number;
        } else if (scan.document_number) {
            invoice = scan.document_number;
        } else if (scan.invoice_date) {
            invoice = `Invoice from ${scan.invoice_date}`;
        } else {
            invoice = `Invoice ${String(scan.id).substring(0, 8)}...`;
        }

        const names = scan.line_items
            .filter(isZeroValueLine)
            .map(item => (item.description || '').trim() || t('common.unnamedItem'));

        return { id: scan.id, invoice, names };
    });
});

const hasZeroValueLine = (scanId) => {
    return zeroValueLineScans.value.some(scan => scan.id === scanId);
};

// Get translated document type
const getDocumentType = (invoiceType) => {
    if (!invoiceType) return t('scanDetails.documentTypes.invoice');

    const typeMap = {
        'Invoice': 'invoice',
        'Credit Note': 'creditNote',
        'Debit Note': 'debitNote',
        'Protocol': 'protocol',
        'Stock Receipt': 'stockReceipt',
        'Purchase Order': 'purchaseOrder',
        'Sales Receipt': 'salesReceipt',
        'Bank Statement': 'bankStatement',
        'Balance Sheet': 'balanceSheet',
        'Income Statement (Profit and Loss Statement)': 'incomeStatement',
        'Cash Flow Statement': 'cashFlowStatement',
        'Tax Declaration': 'taxDeclaration',
        'Contract': 'contract',
        'Inventory Report': 'inventoryReport',
        'Expense Report': 'expenseReport',
        'Payroll Register': 'payrollRegister',
        'Audit Report': 'auditReport',
        'Waybill (or Bill of Lading)': 'waybill',
        'Reconciliation Statement': 'reconciliationStatement',
        'Receipt': 'receipt',
        'Cash Receipt': 'cashReceipt',
        'Proforma Invoice': 'proformaInvoice',
        'Quotation': 'quotation',
        'Journal Voucher': 'journalVoucher',
        'Ledger Entry': 'ledgerEntry',
        'Financial Statement': 'financialStatement',
        'Budget Report': 'budgetReport',
        'Trial Balance': 'trialBalance',
        'Other Document': 'otherDocument'
    };

    const key = typeMap[invoiceType];
    if (key) {
        return t(`scanDetails.documentTypes.${key}`);
    }

    // If invoiceType looks like a translation key, try to translate it
    if (invoiceType.includes('.')) {
        return t(invoiceType);
    }

    // Otherwise return the raw value
    return invoiceType;
};

// Calculate totals by currency
const totalsByCurrency = computed(() => {
    const totals = {};

    props.scans.forEach(scan => {
        const currency = scan.currency || 'BGN';

        if (!totals[currency]) {
            totals[currency] = {
                subtotal: 0,
                vat: 0,
                grand: 0
            };
        }

        totals[currency].subtotal += parseFloat(scan.subtotal) || 0;
        totals[currency].vat += parseFloat(scan.tax_amount) || 0;
        totals[currency].grand += parseFloat(scan.grand_total) || 0;
    });

    return totals;
});

// Group scans by creation date
const scansGroupedByDate = computed(() => {
    const groups = [];
    const dateMap = new Map();

    props.scans.forEach(scan => {
        const date = new Date(scan.created_at);
        const dateKey = date.toDateString(); // e.g., "Tue Dec 17 2025"

        if (!dateMap.has(dateKey)) {
            const today = new Date();
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);

            let label;
            if (date.toDateString() === today.toDateString()) {
                label = t('common.today');
            } else if (date.toDateString() === yesterday.toDateString()) {
                label = t('common.yesterday');
            } else {
                label = date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
                });
            }

            const group = { date: dateKey, label, scans: [] };
            dateMap.set(dateKey, group);
            groups.push(group);
        }

        dateMap.get(dateKey).scans.push(scan);
    });

    return groups;
});

const isSelected = (scanId) => {
    return props.selectedScans.includes(scanId);
};

const toggleSelection = (scanId) => {
    emit('toggle-selection', scanId);
};

const isDateGroupSelected = (group) => {
    return group.scans.every(scan => props.selectedScans.includes(scan.id));
};

const toggleDateGroupSelection = (group) => {
    const allSelected = isDateGroupSelected(group);

    group.scans.forEach(scan => {
        const isCurrentlySelected = props.selectedScans.includes(scan.id);

        if (allSelected && isCurrentlySelected) {
            // Deselect all
            emit('toggle-selection', scan.id);
        } else if (!allSelected && !isCurrentlySelected) {
            // Select all unselected
            emit('toggle-selection', scan.id);
        }
    });
};

const changePage = (page) => {
    if (page >= 1 && page <= props.totalPages) {
        emit('page-change', page);
    }
};

const viewScan = (id) => {
    emit('view', id);
    router.push(`/scans/${id}`);
};

const handleDelete = (scanId) => {
    scanToDelete.value = scanId;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    scanToDelete.value = null;
};

const confirmDelete = () => {
    if (scanToDelete.value) {
        emit('delete', scanToDelete.value);
        closeDeleteModal();
    }
};

const handleKeydown = (event) => {
    if (showDeleteModal.value) {
        if (event.key === 'Escape') {
            closeDeleteModal();
        } else if (event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            confirmDelete();
        }
    }
};

onMounted(() => {
    window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
    window.removeEventListener('keydown', handleKeydown);
});

const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getInvoiceDate = (scan) => {
    // Use invoice_date directly from scan object
    if (scan.invoice_date) {
        return formatDate(scan.invoice_date);
    }
    // Try to get invoice date from result JSON (backward compatibility)
    if (scan.result && scan.result.invoice_date) {
        return formatDate(scan.result.invoice_date);
    }
    // Fallback to created_at
    return formatTimeAgo(scan.created_at);
};

const getSellerTaxId = (scan) => {
    // Priority: VAT number first, then tax ID

    if (scan.seller_tax_id && scan.seller_tax_id.trim()) {
        return scan.seller_tax_id.trim();
    }
    return null;
};
const getSellerVatId = (scan) => {
    // Return VAT number if exists, otherwise fall back to tax ID
    if (scan.seller_vat_number && scan.seller_vat_number.trim()) {
        return scan.seller_vat_number.trim();
    }

    if (scan.seller_tax_id && scan.seller_tax_id.trim()) {
        return scan.seller_tax_id.trim();
    }

    return null;
};

const getSellerVatValidation = (scan) => {
    // Check root level first
    if ('seller_vat_number_valid' in scan) {
        return scan.seller_vat_number_valid;
    }

    // Check for inverse field
    if ('seller_vat_number_invalid' in scan) {
        return !scan.seller_vat_number_invalid;
    }

    // Check inside result object
    if (scan.result && 'seller_vat_number_valid' in scan.result) {
        return scan.result.seller_vat_number_valid;
    }

    return null;
};

// Get counterparty (opposite party) name - seller if we're buyer, buyer if we're seller
const getCounterpartyName = (scan) => {
    const isExpense = scan.type === 'expense' || !scan.type; // Default to expense

    if (isExpense) {
        // We are the buyer, show the seller (our supplier)
        return scan.seller_name || scan.filename || 'Unknown';
    } else {
        // We are the seller, show the buyer (our customer)
        return scan.buyer_name || scan.filename || 'Unknown';
    }
};

// Get counterparty (opposite party) VAT ID
const getCounterpartyVatId = (scan) => {
    const isExpense = scan.type === 'expense' || !scan.type; // Default to expense

    if (isExpense) {
        // We are the buyer, show the seller VAT
        return getSellerVatId(scan);
    } else {
        // We are the seller, show the buyer VAT
        if (scan.buyer_vat_number && scan.buyer_vat_number.trim()) {
            return scan.buyer_vat_number.trim();
        }
        if (scan.buyer_tax_id && scan.buyer_tax_id.trim()) {
            return scan.buyer_tax_id.trim();
        }
        return null;
    }
};

// Get counterparty (opposite party) Tax ID (separate from VAT)
const getCounterpartyTaxId = (scan) => {
    const isExpense = scan.type === 'expense' || !scan.type; // Default to expense

    if (isExpense) {
        // We are the buyer, show the seller Tax ID only if it's different from VAT
        const vatNumber = scan.seller_vat_number ? scan.seller_vat_number.trim() : null;
        const taxId = scan.seller_tax_id ? scan.seller_tax_id.trim() : null;

        // Only return tax ID if it exists AND is different from VAT number
        if (taxId && vatNumber && taxId !== vatNumber) {
            return taxId;
        }
        return null;
    } else {
        // We are the seller, show the buyer Tax ID only if it's different from VAT
        const vatNumber = scan.buyer_vat_number ? scan.buyer_vat_number.trim() : null;
        const taxId = scan.buyer_tax_id ? scan.buyer_tax_id.trim() : null;

        // Only return tax ID if it exists AND is different from VAT number
        if (taxId && vatNumber && taxId !== vatNumber) {
            return taxId;
        }
        return null;
    }
};

// Get counterparty VAT validation status
const getCounterpartyVatValidation = (scan) => {
    const isExpense = scan.type === 'expense' || !scan.type; // Default to expense

    if (isExpense) {
        // We are the buyer, show the seller VAT validation
        return getSellerVatValidation(scan);
    } else {
        // We are the seller, show the buyer VAT validation
        return getBuyerVatValidation(scan);
    }
};

const getBuyerVatValidation = (scan) => {
    // Check root level first
    if ('buyer_vat_number_valid' in scan) {
        return scan.buyer_vat_number_valid;
    }

    // Check for inverse field
    if ('buyer_vat_number_invalid' in scan) {
        return !scan.buyer_vat_number_invalid;
    }

    // Check inside result object
    if (scan.result && 'buyer_vat_number_valid' in scan.result) {
        return scan.result.buyer_vat_number_valid;
    }

    return null;
};

// Generated first-page previews for invoices whose original is a PDF
// (the server thumbnail endpoint only rasterizes images, not PDFs).
const pdfThumbnails = reactive({});
const pdfThumbnailTried = new Set();

const handleThumbnailError = async (event, scanId) => {
    // Hide the broken <img> immediately; we'll swap in a PDF preview if we can.
    if (event?.target) {
        event.target.style.display = 'none';
    }

    if (!scanId || pdfThumbnailTried.has(scanId)) {
        return;
    }
    pdfThumbnailTried.add(scanId);

    try {
        const response = await fetch(`/api/invoices/${scanId}/original`);
        if (!response.ok) return;

        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('pdf')) {
            // Not a PDF — nothing we can render; leave the placeholder gap.
            return;
        }

        const arrayBuffer = await response.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const page = await pdf.getPage(1);

        // Render the first page small — this is just a list thumbnail.
        const viewport = page.getViewport({ scale: 1 });
        const targetWidth = 160;
        const scale = targetWidth / viewport.width;
        const scaledViewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        await page.render({
            canvasContext: canvas.getContext('2d'),
            viewport: scaledViewport,
        }).promise;

        pdfThumbnails[scanId] = canvas.toDataURL('image/jpeg', 0.7);
    } catch (e) {
        // Corrupt/unreadable PDF — keep the placeholder, don't spam the console.
        console.warn('PDF thumbnail render failed for', scanId, e);
    }
};

const handleTypeChange = (scanId, newType) => {
    emit('type-change', { scanId, type: newType });
};

const handleSingleExport = (scanId) => {
    // Emit export event with array containing single scan ID
    emit('export', [scanId]);
};

// Get warning description for a scan
const getWarningDescription = (scanId) => {
    const missing = hasMissingInfo(scanId);
    const conflict = hasVatConflictForScan(scanId);
    const unique = hasUniqueName(scanId);

    const warnings = [];
    if (missing) warnings.push('Missing required information');
    if (conflict) warnings.push('Duplicate VAT number with different name');
    if (unique) warnings.push('Unique partner name (potential outlier)');

    return warnings.join(' • ');
};
</script>

<style scoped>
.scans-table-wrapper {
    width: 100%;
}

/* Loading State */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 100px 24px;
    color: var(--text-secondary);
}

.spinner-large {
    width: 56px;
    height: 56px;
    border: 5px solid var(--border-color);
    border-top-color: var(--primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 20px;
    box-shadow: 0 4px 12px rgba(14, 165, 233, 0.2);
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Empty State */
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 100px 24px;
    text-align: center;
}

.empty-title {
    font-size: 26px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 28px 0 12px 0;
    letter-spacing: -0.02em;
}

.empty-text {
    font-size: 16px;
    color: var(--text-secondary);
    margin: 0 0 32px 0;
    line-height: 1.6;
}

.btn-start-scanning {
    padding: 14px 36px;
    background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 6px 20px rgba(14, 165, 233, 0.35);
    letter-spacing: -0.01em;
}

.btn-start-scanning:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(14, 165, 233, 0.45);
}

/* Scans List */
.scans-list {
    display: flex;
    flex-direction: column;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
    background: white;
}

.date-separator {
    padding: 12px 24px;
    border-bottom: 1px solid #e2e8f0;
    position: sticky;
    top: 0;
    z-index: 10;
    background: #f8fafc;
}

.date-separator:first-child {
    border-top: none;
}

/* Table Headers (Desktop Only) */
.table-headers {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 18px;
    background: #f8fafc;
    border-bottom: 2px solid #e2e8f0;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #64748b;
}

.header-checkbox {
    width: 16px;
    flex-shrink: 0;
}

.header-thumbnail {
    width: 60px;
    flex-shrink: 0;
}

.header-info {
    flex: 1;
    min-width: 200px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.header-sep {
    color: #cbd5e1;
    font-weight: 400;
}

.header-amount {
    width: 140px;
    flex-shrink: 0;
}

.header-status {
    width: 100px;
    flex-shrink: 0;
    text-align: center;
}

.header-actions {
    width: 160px;
    flex-shrink: 0;
    text-align: right;
}

.sortable-header {
    cursor: pointer;
    user-select: none;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: color 0.15s ease;
}

.header-status.sortable-header {
    justify-content: center;
}

.sortable-header:hover {
    color: #334155;
}

.sortable-header .sort-icon {
    font-size: 11px;
    opacity: 0.4;
    transition: opacity 0.15s ease;
}

.sortable-header:hover .sort-icon,
.sortable-header.sort-active .sort-icon {
    opacity: 1;
}

.sortable-header.sort-active {
    color: var(--primary-color, #3b82f6);
}

.date-header {
    display: flex;
    align-items: center;
    gap: 8px;
}

.date-checkbox {
    width: 16px;
    height: 16px;
    cursor: pointer;
}

.date-label {
    font-size: 13px;
    color: var(--text-secondary);
    cursor: pointer;
}

.scan-row {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 18px;
    background: white;
    border-bottom: 1px solid #e2e8f0;
    transition: all 0.2s ease;
    cursor: pointer;
    /* Virtualize long lists: the browser skips rendering/layout for rows that
       are off-screen, keeping large registers (hundreds of rows) smooth.
       contain-intrinsic-size reserves the right scroll height as a placeholder. */
    content-visibility: auto;
    contain-intrinsic-size: auto 72px;
}

.scan-row:hover {
    background: #f8fafc;
    border-left: 3px solid #0ea5e9;
    padding-left: 15px;
}

.scan-row.selected {
    background: #eff6ff;
    border-left: 3px solid #0ea5e9;
    padding-left: 15px;
}

.scan-row.is-checked {
    background: linear-gradient(90deg, #d1fae5 0%, #f0fdf4 30%, #ffffff 100%);
}

.scan-row.is-checked:hover {
    background: linear-gradient(90deg, #d1fae5 0%, #ecfdf5 30%, #f8fafc 100%);
}

.scan-row.is-checked.selected {
    background: linear-gradient(90deg, #a7f3d0 0%, #d1fae5 30%, #f0fdf4 100%);
    box-shadow: -3px 0 0 0 #10b981;
}

.scan-row.has-warning {
    background: linear-gradient(90deg, #ffeaa7 0%, #fff3cd 30%, #ffffff 100%);
    border-left: 3px solid #ffc107;
}

.scan-row.has-warning:hover {
    background: linear-gradient(90deg, #ffde7d 0%, #ffeaa7 30%, #fffbf0 100%);
}

.scan-row.has-warning.selected {
    background: linear-gradient(90deg, #ffde7d 0%, #ffeaa7 30%, #fffbf0 100%);
    box-shadow: -3px 0 0 0 #ffc107;
}

.scan-row.has-warning.is-checked {
    background: linear-gradient(90deg, #d1fae5 0%, #f5fde9 15%, #fff3cd 30%, #ffffff 100%);
}

.scan-row.has-warning.is-checked.selected {
    background: linear-gradient(90deg, #bbf7d0 0%, #ecf9d5 15%, #ffeaa7 30%, #fffbf0 100%);
    box-shadow: -3px 0 0 0 #10b981, -6px 0 0 0 #ffc107;
}

.scan-row:last-child {
    border-bottom: none;
}

/* Archived Row Styles */
.scan-row.status-archived {
    opacity: 0.7;
    background: #f8fafc;
}

.scan-row.status-archived:hover {
    opacity: 0.85;
    background: #f1f5f9;
}

.scan-row.status-archived .invoice-number,
.scan-row.status-archived .company-text {
    color: #64748b;
}

.scan-row.status-archived .amount-value {
    color: #94a3b8;
}

/* Upload Row Styles */
.upload-row {
    background: #fefce8;
    cursor: default;
}

.upload-row:hover {
    background: #fef9c3;
}

.upload-row.status-uploading {
    background: #eff6ff;
}

.upload-row.status-processing {
    background: #fef3c7;
}

.upload-row.status-completed {
    background: #dcfce7;
}

.upload-row.status-failed {
    background: #fee2e2;
}

.checkbox-placeholder {
    width: 16px;
    height: 16px;
}

.upload-progress {
    font-size: 12px;
    font-weight: 600;
    color: var(--primary);
}

.spinner-tiny {
    width: 14px;
    height: 14px;
    border: 2px solid var(--border);
    border-top-color: var(--primary);
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
}

.upload-error {
    font-size: 12px;
    color: #dc2626;
    margin-top: 4px;
}

/* Checkbox */
.scan-checkbox {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.checkbox-input {
    width: 16px;
    height: 16px;
    cursor: pointer;
    border-radius: 6px;
    transition: all 0.2s ease;
}

.checkbox-input:hover {
    transform: scale(1.15);
}

/* Invalid VAT Row Styling */
.scan-row.has-invalid-vat {
    background: linear-gradient(90deg, #fcc 0%, #fee 3%, #ffffff 100%) !important;
    border-left: 4px solid #ef4444 !important;
}

.scan-row.has-invalid-vat:hover {
    background: linear-gradient(90deg, #fbb 0%, #fcc 3%, #fff5f5 100%) !important;
    border-left-color: #dc2626 !important;
    box-shadow: -4px 0 0 0 #dc2626 !important;
    transform: translateX(2px);
}

.scan-row.has-invalid-vat.selected {
    background: linear-gradient(90deg, #fbb 0%, #fcc 30%, #fff5f5 100%) !important;
    box-shadow: -4px 0 0 0 #dc2626 !important;
}

/* Company Validation Warning Row Styling - Purple/Blue */
.scan-row.has-company-warning {
    background: linear-gradient(90deg, #fee2e2 0%, #fef2f2 3%, #ffffff 100%) !important;
    border-left: 4px solid #dc2626 !important;
}

.scan-row.has-company-warning:hover {
    background: linear-gradient(90deg, #fecaca 0%, #fee2e2 3%, #fef5f5 100%) !important;
    border-left-color: #b91c1c !important;
    box-shadow: -4px 0 0 0 #b91c1c !important;
    transform: translateX(2px);
}

.scan-row.has-company-warning.selected {
    background: linear-gradient(90deg, #fecaca 0%, #fee2e2 30%, #fef5f5 100%) !important;
    box-shadow: -4px 0 0 0 #b91c1c !important;
}

/* Zero-value Line Row Styling - Amber, the advisory tone */
.scan-row.has-zero-value-line {
    background: linear-gradient(90deg, #fef3c7 0%, #fffbeb 3%, #ffffff 100%) !important;
    border-left: 4px solid #d97706 !important;
}

.scan-row.has-zero-value-line:hover {
    background: linear-gradient(90deg, #fde68a 0%, #fef3c7 3%, #fffdf5 100%) !important;
    border-left-color: #b45309 !important;
    box-shadow: -4px 0 0 0 #b45309 !important;
    transform: translateX(2px);
}

.scan-row.has-zero-value-line.selected {
    background: linear-gradient(90deg, #fde68a 0%, #fef3c7 30%, #fffdf5 100%) !important;
    box-shadow: -4px 0 0 0 #b45309 !important;
}

/* Total Mismatch Row Styling - Matches warning banner yellow (#fff3cd) */
.scan-row.has-total-mismatch {
    background: linear-gradient(90deg, #fff3cd 0%, #fff3cd 3%, #ffffff 100%) !important;
    border-left: 4px solid #ffc107 !important;
}

.scan-row.has-total-mismatch:hover {
    background: linear-gradient(90deg, #ffe69c 0%, #fff3cd 3%, #fffbf0 100%) !important;
    border-left-color: #f59e0b !important;
    box-shadow: -4px 0 0 0 #f59e0b !important;
    transform: translateX(2px);
}

.scan-row.has-total-mismatch.selected {
    background: linear-gradient(90deg, #ffe69c 0%, #fff3cd 30%, #fffbf0 100%) !important;
    box-shadow: -4px 0 0 0 #f59e0b !important;
}

/* Status Badge */
.scan-status {
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    flex-shrink: 0;
}

.status-completed {
    background: #10b981;
    color: #ffffff;
}

.status-processing {
    background: #f59e0b;
    color: #ffffff;
}

.status-failed {
    background: #ef4444;
    color: #ffffff;
}

.status-pending {
    background: #94a3b8;
    color: #ffffff;
}

.status-archived {
    background: #64748b;
    color: #ffffff;
}

/* Time Badge */
.scan-time {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: var(--text-secondary);
}

.scan-time svg {
    flex-shrink: 0;
    opacity: 0.7;
}

/* Thumbnail */
.scan-thumbnail {
    width: 50px;
    height: 50px;
    border-radius: 8px;
    overflow: hidden;
    background: #f1f5f9;
    border: 2px solid #e2e8f0;
    flex-shrink: 0;
    cursor: pointer;
    transition: all 0.2s ease;
}

.scan-thumbnail:hover {
    border-color: #0ea5e9;
    transform: scale(1.05);
}

.thumbnail-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.thumbnail-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-secondary);
    background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
}

.thumbnail-placeholder svg {
    color: white;
    opacity: 0.9;
}

/* Invoice Info */
.scan-info {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 200px;
    flex: 1;
    cursor: pointer;
}

.invoice-header {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 4px;
}

.invoice-type {
    font-size: 11px;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 4px;
}

.invoice-number {
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
}

.invoice-date-inline {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: var(--text-secondary);
    margin-left: 8px;
    flex-shrink: 0;
}

.invoice-date-inline svg {
    opacity: 0.6;
}

.invoice-details {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 13px;
    color: var(--text-secondary);
    flex-wrap: nowrap;
}

.invoice-details svg {
    flex-shrink: 0;
    opacity: 0.6;
}

.company-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 320px;
    font-size: 14px;
    font-weight: 600;
    color: #334155;
}

.invoice-tax-id-row {
}

.invoice-tax-id-inline {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    color: var(--text-secondary);
    font-weight: 500;
    font-family: 'Courier New', monospace;
    flex-shrink: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
}

.invoice-tax-id-inline svg {
    width: 12px;
    height: 12px;
    font-style: normal;
    color: #0ea5e9;
}

.invoice-tax-id-inline.vat-invalid-text {
    color: #ef4444;
    font-weight: 600;
}

.invoice-tax-id-inline.vat-invalid-text svg {
    color: #ef4444;
}

.invoice-tax-id {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: var(--text-secondary);
    font-weight: 500;
    font-family: 'Courier New', monospace;
}

.invoice-tax-id svg {
    flex-shrink: 0;
    opacity: 0.6;
    color: #6366f1;
}

.invoice-amount-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-end;
    flex-shrink: 0;
    min-width: 140px;
    margin-right: 90px;
}

.invoice-amount {
    display: flex;
    align-items: baseline;
    gap: 6px;
    flex-wrap: wrap;
}

.amount-value {
    font-size: 22px;
    font-weight: 800;
    background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1;
    overflow-wrap: break-word;
    word-break: break-all;
    max-width: 100%;
}

.amount-currency {
    font-size: 16px;
    font-weight: 700;
    color: #64748b;
    line-height: 1;
}

.invoice-date {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    color: var(--text-secondary);
}

.invoice-date svg {
    flex-shrink: 0;
    opacity: 0.6;
    width: 12px;
    height: 12px;
}

.invoice-meta-row {
    display: flex;
    align-items: center;
    gap: 12px;
}

.invoice-payment {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: #64748b;
    font-weight: 600;
}

.invoice-payment svg {
    flex-shrink: 0;
    opacity: 0.7;
    color: #64748b;
}

.invoice-vat {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
}

.vat-label {
    font-weight: 600;
    color: #64748b;
}

.vat-amount {
    font-weight: 600;
    color: #64748b;
}

.invoice-type-selector {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-top: 4px;
}

.type-select {
    width: 100px;
    height: 30px;
    padding: 4px 12px;
    padding-right: 30px;
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    color: var(--text-primary);
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
}

.type-select:hover {
    border-color: var(--primary);
    box-shadow: 0 2px 6px rgba(14, 165, 233, 0.15);
    transform: translateY(-1px);
}

.type-select:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.type-select option {
    padding: 8px;
}

/* Actions */
.scan-actions {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-left: auto;
    flex-shrink: 0;
}

.scan-actions .invoice-amount {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-right: auto;
    gap: 2px;
}

.btn-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    min-width: 32px;
    padding: 0;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
}

.btn-action svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    display: block;
}

.btn-view {
    background: #0ea5e9;
    color: white;
}

.btn-view:hover {
    background: #0284c7;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.btn-export {
    background: #10b981;
    color: white;
}

.btn-export:hover {
    background: #059669;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-action.btn-delete {
    background: #ef4444;
    color: white;
}

.btn-action.btn-delete:hover {
    background: #dc2626;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

/* Pagination */
.pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    margin-top: 32px;
    padding: 24px 0;
}

.pagination-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: white;
    border: 2px solid var(--border-color);
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.pagination-btn:hover:not(:disabled) {
    border-color: var(--primary);
    color: var(--primary);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(14, 165, 233, 0.2);
}

.pagination-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.pagination-pages {
    display: flex;
    gap: 8px;
}

.pagination-page {
    min-width: 44px;
    height: 44px;
    padding: 0 14px;
    background: white;
    border: 2px solid var(--border-color);
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.pagination-page:hover {
    border-color: var(--primary);
    color: var(--primary);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(14, 165, 233, 0.2);
}

.pagination-page.active {
    background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
    border-color: #0ea5e9;
    color: white;
    box-shadow: 0 4px 12px rgba(14, 165, 233, 0.4);
}

/* Totals Summary */
.totals-summary {
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    border-top: 3px solid var(--primary);
    border-radius: 12px;
    padding: 24px;
    margin-top: 24px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.totals-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 16px;
    align-items: stretch;
}

.totals-label {
    font-size: 18px;
    font-weight: 700;
    color: #64748b;
    letter-spacing: -0.01em;
    width: 100%;
    margin-bottom: 12px;
}

.currency-group {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    background: white;
    border-radius: 10px;
    border: 2px solid var(--border);
    flex: 1 1 300px;
    min-width: 280px;
    max-width: 100%;
}

.currency-header {
    font-size: 13px;
    font-weight: 800;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 6px 12px;
    border-bottom: 1px solid var(--border);
    background: #f9fafb;
    border-radius: 6px 6px 0 0;
    margin: -16px -16px 12px -16px;
}

.totals-items {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.total-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 10px;
    background: transparent;
    border-bottom: none;
    transition: all 0.2s ease;
}

.total-item:hover {
    background: rgba(14, 165, 233, 0.05);
}

.total-item.total-grand {
    background: transparent;
    border-top: 2px solid rgba(0, 0, 0, 0.1);
    border-bottom: none;
    padding: 8px 10px;
    margin-top: 2px;
}

.total-label {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-secondary);
    flex-shrink: 0;
}

.total-grand .total-label {
    font-size: 13px;
    font-weight: 700;
    color: #64748b;
}

.total-value {
    font-size: 14px;
    font-weight: 700;
    color: var(--primary);
    text-align: right;
    white-space: nowrap;
}

.total-item:nth-child(2) .total-value {
    color: #64748b;
}

.total-grand .total-value {
    font-size: 16px;
    font-weight: 800;
    color: var(--primary);
}

/* Validation Warning Banner */
.validation-warning-banner {
    background-color: #fff3cd;
    padding: 12px 16px;
    margin-bottom: 16px;
    color: #856404;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 14px;
}

.validation-warning-banner svg {
    display: none;
}

.warning-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.warning-summary {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
}

.validation-warning-banner strong {
    color: #856404;
    margin-right: 4px;
    flex-shrink: 0;
}

.validation-warning-banner span {
    color: #856404;
}

.clickable-warning {
    cursor: pointer;
    text-decoration: underline;
    font-weight: 600;
}

.clickable-warning:hover {
    color: #664d03;
}

.warning-details {
    margin-top: 8px;
    padding: 12px;
    background: transparent;
    border-radius: 6px;
    font-size: 13px;
}

.detail-item {
    padding: 6px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    gap: 8px;
}

.detail-item:last-child {
    border-bottom: none;
}

.detail-item .btn-action.btn-view {
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    padding: 0;
    background: #0ea5e9;
    border-color: #0ea5e9;
    color: white;
}

.detail-item .btn-action.btn-view:hover {
    background: #0284c7;
    border-color: #0284c7;
    box-shadow: 0 4px 16px rgba(14, 165, 233, 0.4);
}

.detail-item .btn-action.btn-view svg {
    color: white;
    width: 16px;
    height: 16px;
}

.detail-item strong {
    display: inline-block;
    margin-right: 8px;
    color: #664d03;
}

.invoice-link {
    text-decoration: none;
    color: #0066cc;
    transition: all 0.2s ease;
}

.invoice-link:hover {
    color: #0052a3;
    text-decoration: underline;
}

.invoice-link strong {
    color: inherit;
}

.missing-fields {
    color: #856404;
    font-style: italic;
}

/* Invalid VAT Warning Banner */
.invalid-vat-warning-banner {
    background-color: #fee;
    padding: 12px 16px;
    margin-bottom: 16px;
    color: #721c24;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
}

.invalid-vat-warning-banner svg {
    display: none;
}

.invalid-vat-warning-banner strong {
    color: #dc3545;
    margin-right: 4px;
    flex-shrink: 0;
}

.invalid-vat-warning-banner span {
    color: #721c24;
}

/* Total Mismatch Warning Banner */
.total-mismatch-warning-banner {
    background-color: #fff3cd;
    padding: 12px 16px;
    margin-bottom: 16px;
    color: #856404;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
}

.total-mismatch-warning-banner svg {
    display: none;
}

.total-mismatch-warning-banner strong {
    color: #856404;
    margin-right: 4px;
    flex-shrink: 0;
}

.total-mismatch-warning-banner span {
    color: #856404;
}


/* Responsive */
@media (max-width: 968px) {
    .table-headers {
        display: none;
    }

    .scans-list {
        border: none;
        border-radius: 0;
        gap: 12px;
        background: transparent;
    }

    .scan-row {
        display: flex;
        flex-direction: column;
        gap: 0;
        padding: 0;
        position: relative;
        min-height: auto;
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        background: white;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .scan-row:hover {
        border-left: 1px solid #e2e8f0;
        padding-left: 0;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }

    .scan-checkbox {
        position: absolute;
        top: 12px;
        left: 12px;
        z-index: 15;
        background: white;
        border-radius: 4px;
        padding: 4px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }

    .scan-status {
        position: absolute;
        top: 12px;
        right: 12px;
        z-index: 10;
        padding: 6px 12px;
        font-size: 11px;
        border-radius: 6px;
        grid-column: unset;
        grid-row: unset;
        margin: 0;
    }

    .scan-thumbnail {
        width: 80px;
        height: 80px;
        border-radius: 8px;
        margin: 48px 0 0 12px;
        align-self: flex-start;
        grid-column: unset;
        grid-row: unset;
        flex-shrink: 0;
    }

    .scan-info {
        margin-left: 104px;
        margin-top: -80px;
        padding: 0 12px 12px 0;
        min-width: 0;
        width: calc(100% - 116px);
    }

    .invoice-header {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
        margin-bottom: 4px;
    }

    .invoice-number {
        font-size: 16px;
        font-weight: 700;
    }

    .invoice-type {
        font-size: 11px;
        background: #f1f5f9;
        padding: 2px 6px;
        border-radius: 4px;
    }

    .invoice-date {
        font-size: 12px;
        color: #64748b;
        margin-bottom: 4px;
    }

    .invoice-details {
        margin-bottom: 4px;
        flex-wrap: wrap;
    }

    .company-text {
        font-size: 14px;
        font-weight: 600;
        color: #334155;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: calc(100% - 20px);
        flex-basis: 100%;
    }

    .invoice-tax-id-row {
        margin-top: 4px;
        width: 100%;
    }

    .invoice-tax-id-inline {
        font-size: 12px;
        color: #64748b;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: inline-flex;
        align-items: center;
        gap: 3px;
        flex-basis: 100%;
        margin-top: 4px;
    }

    .invoice-amount-wrapper {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        background: #f8fafc;
        padding: 14px 12px;
        border-radius: 0;
        margin-top: 0;
        width: 100%;
        border-top: 1px solid #e2e8f0;
    }

    .invoice-amount {
        display: flex;
        flex-direction: row;
        align-items: baseline;
        gap: 4px;
    }

    .amount-value {
        font-size: 24px;
        font-weight: 700;
        color: #0f172a;
    }

    .amount-currency {
        font-size: 16px;
        font-weight: 600;
        color: #64748b;
    }

    .invoice-vat {
        display: none;
    }

    .invoice-payment {
        display: none;
    }

    .invoice-type-selector {
        display: none;
    }

    .scan-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px;
        width: 100%;
        margin-top: 0;
        border-top: 1px solid #e2e8f0;
        background: white;
        border-radius: 0 0 12px 12px;
        justify-content: flex-end;
    }

    .btn-action {
        width: 44px;
        height: 44px;
        min-width: 44px;
        min-height: 44px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        font-size: 18px;
        border: none;
    }

    .btn-action svg {
        width: 20px;
        height: 20px;
    }

    /* Upload rows */
    .upload-row {
        flex-direction: row;
        flex-wrap: wrap;
        min-height: auto;
    }

    .upload-row .scan-checkbox {
        position: static;
        order: 1;
    }

    .upload-row .scan-status {
        position: static;
        order: 2;
        margin-left: auto;
    }

    .upload-row .scan-thumbnail {
        order: 3;
        width: 50px;
        height: 50px;
        margin: 0;
        align-self: center;
    }

    .upload-row .scan-info {
        order: 4;
        margin: 0;
        width: calc(100% - 130px);
    }

    .upload-row .scan-actions {
        order: 5;
        width: 100%;
        margin-top: 12px;
        justify-content: flex-end;
    }

    /* Totals summary */
    .totals-summary {
        padding: 16px;
        margin-top: 16px;
    }

    .totals-container {
        flex-direction: column;
        gap: 12px;
    }

    .currency-group {
        min-width: 100%;
    }

    .total-item {
        flex-direction: row;
        justify-content: space-between;
        padding: 8px 0;
    }

    .total-label {
        font-size: 14px;
    }

    .total-value {
        font-size: 16px;
    }

    .total-grand .total-label {
        font-size: 15px;
    }

    .total-grand .total-value {
        font-size: 18px;
    }
}

/* Unique Partner Name Highlighting */
.company-text.unique-name {
    background-color: #ffebee;
    color: #c62828;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
}

/* Warning Icon */
.warning-icon {
    display: inline-flex;
    align-items: center;
    margin-left: 6px;
    color: #f59e0b;
    font-size: 14px;
    cursor: help;
    width: 14px;
    height: 14px;
}

/* VAT Validation Indicators */
.vat-indicator {
    display: inline-flex;
    align-items: center;
    margin-left: 6px;
    font-size: 14px;
    cursor: help;
}

.vat-indicator.vat-valid {
    color: #10b981;
}

.vat-indicator.vat-invalid {
    color: #ef4444;
}

.invoice-tax-id.vat-invalid-text {
    color: #ef4444;
    font-weight: 600;
}

.invoice-tax-id.vat-invalid-text svg {
    color: #ef4444;
}

/* Delete Confirmation Modal */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    border-radius: 12px;
    padding: 24px;
    max-width: 400px;
    width: 90%;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    position: relative;
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e2e8f0;
    position: relative;
}

.modal-header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
    padding-right: 40px;
}

.modal-header .btn {
    position: absolute;
    top: 0;
    right: 0;
    flex-shrink: 0;
}

.modal-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    border-top: none;
    padding-top: 0;
}

.btn-confirm,
.btn-cancel {
    padding: 10px 20px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-confirm {
    background: #ef4444;
    color: white;
}

.btn-confirm:hover {
    background: #dc2626;
}

.btn-cancel {
    background: #f1f5f9;
    color: #64748b;
}

.btn-cancel:hover {
    background: #e2e8f0;
}

.keyboard-hint {
    font-size: 11px;
    opacity: 0.7;
    font-weight: 400;
}
</style>
